import { useMutation } from '@apollo/client/react/hooks';
import InfoHollow from '@clearkit/icons/glyphs/InfoHollow';
import Spinner from '@clearkit/icons/indicators/Spinner';
import {
  CKBadge,
  CKButton,
  CKCardCollapsible,
  CKModal,
  CKSwitch,
} from '@clearkit/react';
import capitalize from 'lodash/capitalize';
import React, { useState } from 'react';

import ToastsContainer from '~/containers/Toasts';
import { readableType } from '~/lib/dataEnrichment';

import toggle from './toggle.graphql';

const EnrichmentToggle = ({
  integration,
  objectType,
  loading,
  isActive,
  onToggleChange,
  accountConnector,
  hasEnrichmentBetaUIFlag,
  hasSavedSelectiveEnrichmentConfig,
}) => {
  const [toggleFieldMapping, { loading: isToggling }] = useMutation(toggle, {
    onCompleted: () => {
      ToastsContainer.addSuccess(
        `${capitalize(objectType.name)} enrichment ${
          isActive ? 'disabled' : 'enabled'
        }`,
      );
      onToggleChange(isActive ? false : true);
    },
    onError: () => {
      ToastsContainer.addWarning(
        `An error occured when ${isActive ? 'disabling' : 'enabling'} ${
          objectType.name
        } enrichment, please try again.`,
      );
    },
    refetchQueries: ['FieldMappingForm'],
    variables: {
      input: {
        id: accountConnector?.id,
        targetObjectType: capitalize(objectType.name),
        enabled: !isActive,
      },
    },
  });

  const [confirmToggleDialogOpen, setConfirmToggleDialogOpen] = useState(false);

  return (
    <>
      {hasEnrichmentBetaUIFlag ? (
        <SelectiveEnrichmentToggle
          integration={integration}
          objectType={objectType}
          loading={loading}
          isActive={isActive}
          hasSavedSelectiveEnrichmentConfig={hasSavedSelectiveEnrichmentConfig}
          setConfirmToggleDialogOpen={setConfirmToggleDialogOpen}
        />
      ) : (
        <BaseEnrichmentToggle
          integration={integration}
          objectType={objectType}
          isActive={isActive}
          loading={loading}
          setConfirmToggleDialogOpen={setConfirmToggleDialogOpen}
        />
      )}
      <ConfirmationModal
        confirmToggleDialogOpen={confirmToggleDialogOpen}
        setConfirmToggleDialogOpen={setConfirmToggleDialogOpen}
        isActive={isActive}
        integration={integration}
        objectType={objectType}
        toggleFieldMapping={toggleFieldMapping}
        isToggling={isToggling}
      />
    </>
  );
};

function SelectiveEnrichmentToggle({
  integration,
  objectType,
  loading,
  isActive,
  hasSavedSelectiveEnrichmentConfig,
  setConfirmToggleDialogOpen,
}) {
  return (
    <div className="px-4 mt-10">
      <CKCardCollapsible
        header={
          <SelectiveEnrichmentToggleHeader
            integration={integration}
            objectType={objectType}
            isActive={isActive}
          />
        }
        footerClasses="bg-gray-0"
        footer={
          hasSavedSelectiveEnrichmentConfig ? (
            <SelectiveEnrichmentToggleFooter
              integration={integration}
              objectType={objectType}
            />
          ) : null
        }>
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-col mr-20 w-96">
            <p className="text-base font-medium text-gray-800">
              Enable or disable {objectType.name} enrichment
            </p>
          </div>
          {loading ? (
            <Spinner className="mx-4" />
          ) : (
            <CKSwitch
              id="field mapping status"
              checked={isActive}
              onChange={() => setConfirmToggleDialogOpen(true)}>
              {isActive ? 'Enabled' : 'Disabled'}
            </CKSwitch>
          )}
        </div>
      </CKCardCollapsible>
    </div>
  );
}

function SelectiveEnrichmentToggleFooter({ integration, objectType }) {
  return (
    <div className="flex flex-row items-center ">
      <div className="flex flex-col mr-4">
        <InfoHollow />
      </div>
      Selective sync is on. Enriched {objectType.name} data will only be synced
      to {readableType(integration)} for {objectType.name}s that meet your
      selective sync criteria.
    </div>
  );
}

function SelectiveEnrichmentToggleHeader({
  integration,
  objectType,
  isActive,
}) {
  return (
    <>
      <div
        className="grid grid-cols-[1fr,auto]"
        data-testid="enrichmentToggleConfigCardHeader">
        <div className="text-base">
          <h3 className="mb-2 font-medium">
            {capitalize(objectType.name)} enrichment
          </h3>
          <p>
            Enable {objectType.name} enrichment to{' '}
            <span className="font-medium">
              sync enriched {objectType.name} data to {capitalize(integration)}
            </span>{' '}
            and keep previously enriched {objectType.name}s up-to-date with
            fresh data, using the field mappings you’ve set up for{' '}
            {capitalize(integration)} {objectType.name}s.
          </p>
        </div>
        {isActive ? (
          <CKBadge
            className="self-start text-xs text-green-700 bg-green-100"
            variant="default">
            Enabled
          </CKBadge>
        ) : (
          <CKBadge
            className="self-start text-xs text-gray-600 bg-gray-100"
            variant="default">
            Disabled
          </CKBadge>
        )}
      </div>
    </>
  );
}

function BaseEnrichmentToggle({
  integration,
  objectType,
  isActive,
  loading,
  setConfirmToggleDialogOpen,
}) {
  return (
    <React.Fragment>
      <div className="flex flex-row items-center justify-between pb-4 border-b border-gray-100">
        <div className="flex flex-col mr-20 w-96">
          <p className="mb-2 text-base font-medium text-gray-800">
            Enrich {readableType(integration)} {objectType.name} fields
          </p>
          <p className="max-w-3xl text-sm text-gray-600">
            When enabled, new {objectType.name}s in {readableType(integration)}{' '}
            will be enriched with the data you’ve mapped from Clearbit.
          </p>
        </div>
        {loading ? (
          <Spinner className="mx-4" />
        ) : (
          <CKSwitch
            id="field mapping status"
            checked={isActive}
            onChange={() => setConfirmToggleDialogOpen(true)}>
            {isActive ? 'Enabled' : 'Disabled'}
          </CKSwitch>
        )}
      </div>
    </React.Fragment>
  );
}

function ConfirmationModal({
  confirmToggleDialogOpen,
  setConfirmToggleDialogOpen,
  isActive,
  integration,
  objectType,
  toggleFieldMapping,
  isToggling,
}) {
  return (
    <CKModal
      baseId="toggle-field-mapping-modal"
      visible={confirmToggleDialogOpen}
      hide={() => setConfirmToggleDialogOpen(false)}
      size="sm"
      aria-label="Modal">
      <CKModal.Header>
        <CKModal.Heading>
          Are you sure you want to {isActive ? 'disable' : 'enable'} enriching{' '}
          {readableType(integration)} {objectType.name}s?
        </CKModal.Heading>
      </CKModal.Header>
      <CKModal.Body className="px-10 py-6">
        {isActive
          ? `${readableType(integration)} ${
              objectType.name
            }s previously enriched by Clearbit data will
      retain that data.`
          : `${readableType(integration)} ${
              objectType.name
            }s will be enriched with Clearbit data.`}
      </CKModal.Body>
      <CKModal.Footer>
        <CKButton
          className="mr-2"
          onClick={() => setConfirmToggleDialogOpen(false)}>
          Cancel
        </CKButton>
        <CKButton
          isLoading={isToggling}
          onClick={async () => {
            await toggleFieldMapping();
            setConfirmToggleDialogOpen(false);
          }}
          variant={CKButton.Variant.bold}
          variantColor="red">
          Yes, {isActive ? 'disable' : 'enable'} syncing data to{' '}
          {objectType.name}s
        </CKButton>
      </CKModal.Footer>
    </CKModal>
  );
}

export default EnrichmentToggle;
